<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters class="align-baseline px-3">
        <v-col cols="auto">
          <v-checkbox @change="changeLayerVisibility" v-model="isVisible" color="primary"> </v-checkbox>
        </v-col>
        <v-col cols="4" class="text-left text-body-2">
          {{ translatedLayerName }}
        </v-col>
        <v-col cols="5">
          <data-input
            v-model="selectedParameter"
            :items="computedParameterTypeRangesArray"
            return-object
            :data-type="dataType"
            :hint="$i18n.t('dialog.body.chooseParameter')"
            persistent-hint
          >
          </data-input>
        </v-col>
        <v-spacer />
        <v-col>
          <v-tooltip bottom color="font">
            <template #activator="{ on: onTooltip }">
              <v-btn small icon @click="changeLabelsVisibility" v-on="onTooltip">
                <v-icon
                  color="font"
                  style="height: 19px; width: 19px; transform: scale(1.3)"
                  v-text="isLabelsVisible ? '$labelActive' : '$label'"
                />
              </v-btn>
            </template>
            {{ isLabelsVisible ? $i18n.t('button.turnOffLabels') : $i18n.t('button.turnOnLabels') }}
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row no-gutters class="justify-start px-3 mt-n3" v-if="computedRanges">
        <v-col cols="auto">
          <v-row no-gutters class="align-center" v-if="selectedParameter.abs">
            <v-col cols="auto" class="pr-1 ml-8" style="color: rgba(0,0,0,0.59)">
              <italic-subheader translation-path="sidebar.absoluteValues" class="pb-3" />
            </v-col>
          </v-row>
          <v-row no-gutters class="align-center" v-for="(range, index) in computedRanges" :key="range">
            <v-col cols="auto" class="pr-1 ml-8">
              <layer-style-preview-icon
                @click="openStyleDialog(index)"
                interactive
                is-icon
                :layer-type="symbolizationKind"
                :layerStyle="
                  symbolizationKind === 'point'
                    ? { 'circle-color': modelingFeatureColors[index] }
                    : { 'line-color': modelingFeatureColors[index] }
                "
              />
            </v-col>
            <v-col cols="auto" class="text-body-2">
              {{ range }}
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
    <main-input-dialog
      v-if="isStyleDialogLoaded"
      :is-visible.sync="isStyleDialogVisible"
      :title-translation="
        $i18n.t(this.type === 'nodes' ? 'dialog.title.changeNodeStyle' : 'dialog.title.changeLinkStyle')
      "
      agree-text="dialog.agreeSave"
      :is-input-dialog="true"
      @agree="handleChangeStyle"
      :inputs="computedStyleInputs"
      dialog-width="35%"
    >
      <template v-for="input in computedStyleInputs" #[`${input.name}.input`]="slot">
        <v-col :key="input.name">
          <v-row no-gutters>
            <v-col cols="12">
              <data-input v-model="styleProperties[input.name]" v-bind="slot" :cols="7" :nameCols="5"></data-input>
            </v-col>
          </v-row>
        </v-col>
      </template>
      <template #lowerForm="{ isFormValid }">
        <v-row no-gutters>
          <style-dialog-style-preview
            class="justify-center align-center"
            :layer-style="templateStyle"
            :geometry-type="symbolizationKind"
            :is-style-valid="isFormValid"
          />
        </v-row>
      </template>
    </main-input-dialog>
  </v-row>
</template>

<script>
import { get } from 'vuex-pathify';
import {
  ranges,
  defaultFeatureColors,
  defaultPointRadiuses,
  defaultLineDashes,
  defaultLineWidth,
} from '@/assets/js/modelingResults';
import style from '@/mixins/style';

import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import LayerStylePreviewIcon from '@/components/LayerStylePreviewIcon';
import StyleDialogStylePreview from '@/components/StyleDialogStylePreview';

export default {
  name: 'ModelingResultsLayer',
  mixins: [style],
  components: {
    DataInput,
    ItalicSubheader,
    LayerStylePreviewIcon,
    StyleDialogStylePreview,
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    netType: {
      type: String,
      required: true,
    },
    scenarioQualityType: {
      type: String,
      default: 'none',
    },
  },
  data: () => ({
    isStyleDialogLoaded: false,
    isStyleDialogVisible: false,
    styleProperties: {
      'circle-color': null,
      'circle-radius': null,
      'line-dash': [],
      'line-color': null,
      'line-width': null,
    },
    modelingFeatureSizes: null,
    modelingFeatureColors: JSON.parse(JSON.stringify(defaultFeatureColors)),
    modelingLineDashes: JSON.parse(JSON.stringify(defaultLineDashes)),
    currentIndex: null,
    isVisible: true,
    isLabelsVisible: false,
    selectedParameter: undefined,
    dataType: {
      name: 'select',
    },
  }),
  computed: {
    settings: get('admin/settingsValues'),
    rangesSettings: get(`admin/settingsValues@:rangesSettingName`),
    rangesSettingName() {
      return `${this.netType}_modeling_legend_ranges`;
    },
    symbolizationKind() {
      return this.type === 'nodes' ? 'point' : 'linestring';
    },
    translatedLayerName() {
      return this.$i18n.t(`sidebar.${this.type}`);
    },
    parameterTypeRanges() {
      return ranges[this.netType][this.type];
    },
    parameterTypeRangesArray() {
      return this.$_objectToArray(this.parameterTypeRanges);
    },
    computedPointStyleInputs() {
      return this.styleItems.point.filter(item => ['circle-color', 'circle-radius'].includes(item.name));
    },
    computedLinestringStyleInputs() {
      return this.styleItems.linestring.filter(item => ['line-dash', 'line-color', 'line-width'].includes(item.name));
    },
    computedStyleInputs() {
      return this.symbolizationKind === 'point' ? this.computedPointStyleInputs : this.computedLinestringStyleInputs;
    },
    templateStyle() {
      return this.symbolizationKind === 'point'
        ? {
            'circle-color': this.styleProperties['circle-color'],
            'circle-radius': this.styleProperties['circle-radius'],
          }
        : {
            'line-dash': this.styleProperties['line-dash'],
            'line-color': this.styleProperties['line-color'],
            'line-width': this.styleProperties['line-width'],
          };
    },
    computedParameterTypeRangesArray() {
      return this.parameterTypeRangesArray
        .filter(parameter => {
          const settingsEnabled = parameter.settingsNames
            ? parameter.settingsNames.every(setting => this.settings[setting])
            : true;
          const isCorrectQualityType = parameter.qualityName
            ? parameter.qualityName === this.scenarioQualityType
            : true;
          return settingsEnabled && isCorrectQualityType;
        })
        .map(parameter => {
          const { name, unit } = parameter;
          return {
            ...parameter,
            text: `${this.$i18n.t(`sidebar.${name}`)} ${unit ? `[${unit}]` : ''}`,
            ranges: this.rangesSettings?.[this.type]?.[name]?.split(';') || parameter.ranges,
          };
        });
    },
    computedRanges() {
      if (!this.selectedParameter) {
        return false;
      }
      return this.prepareRanges(this.selectedParameter.ranges);
    },
  },
  watch: {
    selectedParameter(nV) {
      this.$root.$emit('setModelingResultsStyle', this.type, nV, this.isLabelsVisible, this.getFeatureStylePayload());
    },
    symbolizationKind: {
      immediate: true,
      handler(nV) {
        if (!this.modelingFeatureSizes) {
          this.modelingFeatureSizes = JSON.parse(
            JSON.stringify(nV === 'point' ? defaultPointRadiuses : defaultLineWidth)
          );
        }
      },
    },
  },
  methods: {
    getFeatureStylePayload() {
      return {
        colors: this.modelingFeatureColors,
        sizes: this.modelingFeatureSizes,
        ...(this.symbolizationKind === 'linestring' && { dashes: this.modelingLineDashes }),
      };
    },
    openStyleDialog(index) {
      this.currentIndex = index;
      if (this.symbolizationKind === 'point') {
        this.styleProperties['circle-color'] = this.modelingFeatureColors[index];
        this.styleProperties['circle-radius'] = this.modelingFeatureSizes[index];
      } else {
        this.styleProperties['line-color'] = this.modelingFeatureColors[index];
        this.styleProperties['line-width'] = this.modelingFeatureSizes[index];
        this.styleProperties['line-dash'] = this.modelingLineDashes[index];
      }
      this.isStyleDialogLoaded = true;
      this.isStyleDialogVisible = true;
    },
    handleChangeStyle() {
      if (this.symbolizationKind === 'point') {
        this.modelingFeatureColors[this.currentIndex] = this.styleProperties['circle-color'];
        this.modelingFeatureSizes[this.currentIndex] = this.styleProperties['circle-radius'];
      } else {
        this.modelingFeatureColors[this.currentIndex] = this.styleProperties['line-color'];
        this.modelingFeatureSizes[this.currentIndex] = this.styleProperties['line-width'];
        this.modelingLineDashes[this.currentIndex] = this.styleProperties['line-dash'];
      }
      this.$root.$emit(
        'setModelingResultsStyle',
        this.type,
        this.selectedParameter,
        !this.isLabelsVisible,
        this.getFeatureStylePayload()
      );
      const currentStorage = JSON.parse(localStorage.getItem('modelingResultsStyles')) || {};
      localStorage.setItem(
        'modelingResultsStyles',
        JSON.stringify({
          ...currentStorage,
          [this.netType]: {
            ...(currentStorage[this.netType] || {}),
            [this.$route.params.scenarioId]: {
              ...(currentStorage[this.netType]?.[this.$route.params.scenarioId] || {}),
              [this.type]: {
                colors: this.modelingFeatureColors,
                sizes: this.modelingFeatureSizes,
                ...(this.symbolizationKind === 'linestring' && { dashes: this.modelingLineDashes }),
              },
            },
          },
        })
      );
      this.isStyleDialogVisible = false;
    },
    changeLayerVisibility() {
      this.$root.$emit('changeLayerVisibility', { layerId: this.type, visible: this.isVisible });
    },
    changeLabelsVisibility() {
      this.$root.$emit(
        'setModelingResultsStyle',
        this.type,
        this.selectedParameter,
        !this.isLabelsVisible,
        this.getFeatureStylePayload()
      );
      this.isLabelsVisible = !this.isLabelsVisible;
    },
    prepareRanges(ranges) {
      return [...ranges, '>'].map((value, index, array) => {
        if (index === 0) {
          return `< ${value}`;
        } else if (index === array.length - 1) {
          return `${value} ${array[index - 1]}`;
        } else {
          return `${array[index - 1]} - ${value}`;
        }
      });
    },
  },
  mounted() {
    this.selectedParameter = this.computedParameterTypeRangesArray[0];
    const currentStorage =
      JSON.parse(localStorage.getItem('modelingResultsStyles'))?.[this.netType]?.[this.$route.params.scenarioId]?.[
        this.type
      ] || null;
    if (currentStorage) {
      this.modelingFeatureSizes = currentStorage.sizes;
      this.modelingFeatureColors = currentStorage.colors;
      this.modelingLineDashes = currentStorage.dashes;
    }
  },
};
</script>
